/**
 * Data Bundle API Service
 */

import api from '../../../services/api';
import type {
  DataBundle,
  DataBundleFile,
  CreateBundleRequest,
  UpdateBundleRequest,
  AddCloudFilesRequest,
  AddCloudFilesResponse,
  AddSyncFolderRequest,
  SyncStats,
  ResearchResult,
  SaveResearchResponse,
} from '../types';

const API_BASE = '/data-bundles';

export class DataBundleApi {
  static async listBundles(): Promise<{ data_bundles: DataBundle[]; total: number }> {
    const response = await api.get(API_BASE);
    return response.data;
  }

  static async listAvailableBundles(): Promise<{ data_bundles: DataBundle[]; total: number }> {
    const [ownedRes, sharedRes] = await Promise.allSettled([
      api.get(API_BASE),
      api.get(`${API_BASE}/shared`),
    ]);
    const owned: DataBundle[] = ownedRes.status === 'fulfilled' ? (ownedRes.value.data.data_bundles || []) : [];
    const shared: DataBundle[] = sharedRes.status === 'fulfilled' ? (sharedRes.value.data.data_bundles || []) : [];
    const seen = new Set(owned.map(b => b.data_bundle_id));
    const taggedShared = shared.filter(b => !seen.has(b.data_bundle_id)).map(b => ({ ...b, is_shared: true }));
    const merged = [...owned, ...taggedShared];
    return { data_bundles: merged, total: merged.length };
  }

  static async getBundle(bundleId: string): Promise<DataBundle> {
    const response = await api.get(`${API_BASE}/${bundleId}`);
    return response.data.data_bundle;
  }

  static async createBundle(data: CreateBundleRequest): Promise<DataBundle> {
    const response = await api.post(API_BASE, data);
    return response.data.data_bundle;
  }

  static async updateBundle(bundleId: string, data: UpdateBundleRequest): Promise<DataBundle> {
    const response = await api.patch(`${API_BASE}/${bundleId}`, data);
    return response.data.data_bundle;
  }

  static async deleteBundle(bundleId: string): Promise<void> {
    await api.delete(`${API_BASE}/${bundleId}`);
  }

  // File operations
  static async uploadFile(bundleId: string, file: File): Promise<DataBundleFile> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`${API_BASE}/${bundleId}/files/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.bundle_file;
  }

  /**
   * Add files this tenant already holds — how "Add from the Library" works here.
   *
   * A bundle is not a Library link subject; it holds files. So a Library file
   * joins a bundle as the same file_id, with no second copy of the bytes and no
   * new kind of relationship invented to make the button work. Removing it from
   * the bundle later leaves the Library untouched.
   */
  static async attachExistingFiles(
    bundleId: string,
    fileIds: string[],
  ): Promise<{ added: DataBundleFile[]; skipped: { file_id: string; reason: string }[] }> {
    const response = await api.post(`${API_BASE}/${bundleId}/files/existing`, { file_ids: fileIds });
    return { added: response.data.added || [], skipped: response.data.skipped || [] };
  }

  static async addCloudFiles(bundleId: string, data: AddCloudFilesRequest): Promise<AddCloudFilesResponse> {
    const response = await api.post(`${API_BASE}/${bundleId}/files/cloud`, data);
    return { bundle_files: response.data.bundle_files, skipped: response.data.skipped || [] };
  }

  static async removeFile(bundleId: string, fileId: string): Promise<void> {
    await api.delete(`${API_BASE}/${bundleId}/files/${fileId}`);
  }

  // Sync folders
  static async addSyncFolder(bundleId: string, data: AddSyncFolderRequest): Promise<DataBundle> {
    const response = await api.post(`${API_BASE}/${bundleId}/sync-folders`, data);
    return response.data.data_bundle;
  }

  static async removeSyncFolder(bundleId: string, folderId: string): Promise<DataBundle> {
    const response = await api.post(`${API_BASE}/${bundleId}/sync-folders/remove`, { folder_id: folderId });
    return response.data.data_bundle;
  }

  static async syncBundle(bundleId: string): Promise<SyncStats> {
    const response = await api.post(`${API_BASE}/${bundleId}/sync`);
    return response.data.sync_stats;
  }

  static async updateAutoSync(bundleId: string, intervalMinutes: number | null): Promise<DataBundle> {
    const response = await api.patch(`${API_BASE}/${bundleId}/auto-sync`, {
      auto_sync_interval_minutes: intervalMinutes,
    });
    return response.data.data_bundle;
  }

  // Context prompt generation
  static async generateContextPrompt(bundleId: string): Promise<DataBundle> {
    const response = await api.post(`${API_BASE}/${bundleId}/generate-context-prompt`);
    return response.data.data_bundle;
  }

  // Web research
  static async researchUrl(
    url: string,
    goal: string,
    navigationHint: string = '',
    guidance: string = '',
  ): Promise<ResearchResult> {
    const response = await api.post('/web-research/research', {
      url,
      goal,
      navigation_hint: navigationHint,
      guidance,
    });
    return response.data;
  }

  static async saveResearchToBundle(
    content: string,
    title: string,
    bundleId: string,
    sourceUrl?: string,
    sources?: string[]
  ): Promise<SaveResearchResponse> {
    const response = await api.post('/web-research/save-to-bundle', {
      content,
      title,
      bundle_id: bundleId,
      source_url: sourceUrl,
      sources: sources,
    });
    return response.data;
  }

  // Library-world sibling of saveResearchToBundle: the target is a Library
  // folder (modules.library tenants), the file lands as a draft placement.
  static async saveResearchToFolder(
    content: string,
    title: string,
    folderId: string,
    sourceUrl?: string,
    sources?: string[]
  ): Promise<{ placement: Record<string, unknown>; outcome: string; file_name: string }> {
    const response = await api.post('/web-research/save-to-folder', {
      content,
      title,
      folder_id: folderId,
      source_url: sourceUrl,
      sources: sources,
    });
    return response.data;
  }

  static async saveResearchToBot(
    content: string,
    title: string,
    botId: string,
    sourceUrl?: string
  ): Promise<{ attachment: Record<string, string>; file_name: string }> {
    const response = await api.post('/web-research/save-to-bot', {
      content,
      title,
      bot_id: botId,
      source_url: sourceUrl,
    });
    return response.data;
  }

  static async saveResearchToTenant(
    content: string,
    title: string,
    tenantId: string,
    sourceUrl?: string
  ): Promise<{ attachment: Record<string, string>; file_name: string }> {
    const response = await api.post('/web-research/save-to-tenant', {
      content,
      title,
      tenant_id: tenantId,
      source_url: sourceUrl,
    });
    return response.data;
  }
}
